import { Component, OnInit, inject, signal, HostListener, LOCALE_ID, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SafeUrl } from '@angular/platform-browser';
import { ProductService } from '../../../../../services/product/product.service';
import { ProductInfo } from '../../product-info/product-info';
import { ProductCurrentStock } from '../../product-current-stock/product-current-stock';
import { ProductHistoryStock } from '../../product-history-stock/product-history-stock';
import { ProductsFileMgt } from '../../../../../services/product/file-management/products.file-mgt.service';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ImageCarousel } from '../../utils/image-carousel/image-carousel';
import { finalize, forkJoin } from 'rxjs';
import { Error } from '../../../../public-user-component/auth/shared/error/error';
import { SuccessfulNotification } from '../../../../../utils/successful-notification/successful-notification';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductConfiguration } from '../../product-configuration/product-configuration';

@Component({
  selector: 'app-sub-product-details',
  standalone: true,
  imports: [
    ProductInfo,
    ProductCurrentStock,
    ProductHistoryStock,
    CommonModule,
    ErrorData,
    ImageCarousel,
    Error,
    SuccessfulNotification,
    ConfirmationDialog,
    ProductConfiguration,
  ],
  templateUrl: './sub-product-details.html',
  styleUrl: './sub-product-details.scss',
})
export class SubProductDetails implements OnInit {
  private productService = inject(ProductService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);

  errorLoadingSubProduct = signal<string>('');
  apiError = signal<string>('');
  showSuccess = signal<boolean>(false);
  showDeleteDialog = signal(false);
  loadingDelete = signal<boolean>(false);
  errorDelete = signal<string>('');

  subProduct = signal<any | null>(null);
  isLoading = signal(true);
  activeTab = signal<'details' | 'current-stock' | 'history-stock' | 'configuration'>('details');
  isDropdownOpen = signal<boolean>(false);
  productImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  subProductImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);
  loadingActivation = signal<boolean>(false);
  confirmationActivationModal = signal<boolean>(false);
  activationType = signal<'activate-sub' | 'deactivate-sub' | null>(null);
  errorActivation = signal<string>('');
  notificationSuccess = signal<boolean>(false);
  productId = signal<number | null>(null);
  isActivated = computed(() => this.subProduct()?.isActive);

  isArabic = computed(() => this.locale.startsWith('ar'));

  ngOnInit() {
    const pId = this.route.snapshot.params['productId'];
    const sId = this.route.snapshot.params['subId'];
    this.productId.set(pId ? +pId : null);
    if (pId && sId) {
      this.loadSubProductData(+pId, +sId);
    }
  }

  loadSubProductData(productId: number, subId: number) {
    this.isLoading.set(true);
    this.productService
      .getSubProductById(productId, subId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.subProduct.set(data);
          this.productImageUrl.set({ url: '', isImage: false });
          if (data.profileImageFid) {
            this.fetchImage(data.profileImageFid).subscribe({
              next: (img) => this.productImageUrl.set(img),
              error: () => this.productImageUrl.set({ url: '', isImage: false }),
            });

            if (data.imagesFids?.length! > 1) {
              const imageRequests = data.imagesFids!.slice(1).map((fid: string) => this.fetchImage(fid));
              forkJoin(imageRequests).subscribe({
                next: (images) => this.subProductImages.set(images as any),
                error: () => this.subProductImages.set([]),
              });
            }
          }
        },
        error: (err) => {
          this.productImageUrl.set({ url: '', isImage: false });
          this.errorLoadingSubProduct.set(
            this.isArabic() ? err.error?.messageAr || 'حدث خطأ ما!' : err.error?.messageEn || 'Some error occurred!',
          );
        },
      });
  }

  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }

  setActiveTab(tab: 'details' | 'current-stock' | 'history-stock' | 'configuration') {
    this.activeTab.set(tab);
  }

  handleAction(action: string) {
    const data = this.subProduct();
    if (!data) return;

    if (action === 'Edit Sub-Product') {
      this.router.navigate(['/admin/products/edit-sub-product', data.product.id, data.id]);
    } else if (action === 'Delete Product') {
      this.handleDeleteAction();
    } else if (action === 'Activate Product' || action === 'Deactivate Product') {
      this.handleActivationAction(action);
    }

    this.isDropdownOpen.set(false);
  }

  handleDeleteAction() {
    const data = this.subProduct();
    if (!data) return;

    this.apiError.set('');
    this.errorDelete.set('');
    this.showDeleteDialog.set(true);
  }

  confirmDelete() {
    const data = this.subProduct();
    if (!data) return;

    this.loadingDelete.set(true);
    this.errorDelete.set('');

    this.productService
      .deleteSubProduct(data.product.id, data.id)
      .pipe(
        finalize(() => this.loadingDelete.set(false)),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: () => {
          this.showDeleteDialog.set(false);
          this.router.navigate(['/admin/products/view-product', data.product.id], {
            state: { deletedType: 'sub-product' },
          });
          this.showSuccess.set(true);
          setTimeout(() => {
            this.showSuccess.set(false);
          }, 2000);
        },
        error: (err) => {
          const errorMessage = this.isArabic()
            ? err.error?.messageAr || err.messageAr || 'حدث خطأ أثناء الحذف.'
            : err.error?.messageEn || err.messageEn || 'An error occurred while deleting.';
          this.errorDelete.set(errorMessage);
        },
      });
  }

  activateSubProduct() {
    const p = this.subProduct();
    if (!p) return;
    this.loadingActivation.set(true);
    this.productService
      .activateSubProduct(this.productId()!, p.id)
      .pipe(
        finalize(() => {
          this.loadingActivation.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.subProduct.set({ ...p, isActive: true });
          this.confirmationActivationModal.set(false);
          this.notificationSuccess.set(true);
          setTimeout(() => {
            this.notificationSuccess.set(false);
            this.activationType.set(null);
          }, 2000);
        },
        error: (err) => {
          this.errorActivation.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occured!',
          );
        },
      });
  }

  deactivateSubProduct() {
    const p = this.subProduct();
    if (!p) return;
    this.loadingActivation.set(true);

    this.productService
      .deactivateSubProduct(this.productId()!, p.id)
      .pipe(
        finalize(() => {
          this.loadingActivation.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.subProduct.set({ ...p, isActive: false });
          this.confirmationActivationModal.set(false);
          this.notificationSuccess.set(true);
          setTimeout(() => {
            this.notificationSuccess.set(false);
            this.activationType.set(null);
          }, 2000);
        },
        error: (err) => {
          this.errorActivation.set(
            this.isArabic() ? err.error.messageAr || 'حدث بعض الخطأ!' : err.error.messageEn || 'some error occured',
          );
        },
      });
  }

  handleActivationAction(action: 'Activate Product' | 'Deactivate Product') {
    this.confirmationActivationModal.set(true);
    this.activationType.set(action === 'Activate Product' ? 'activate-sub' : 'deactivate-sub');
  }

  confirmationModalContent = computed(() => {
    const ar = this.isArabic();
    const type = this.activationType();
    const loading = this.loadingActivation();

    const content = {
      'deactivate-sub': {
        title: ar ? 'تأكيد إلغاء التفعيل' : 'Confirm Deactivation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في إلغاء تفعيل هذا الصنف؟'
          : 'Are you sure you want to deactivate this product?',
        confirmContent: ar
          ? loading
            ? 'جارٍ إلغاء التفعيل...'
            : 'تأكيد إلغاء التفعيل'
          : loading
            ? 'Deactivating...'
            : 'Confirm Deactivation',
      },
      'activate-sub': {
        title: ar ? 'تأكيد تفعيل الصنف الفرعي' : 'Confirm Sub Product Activation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في تفعيل هذا الصنف الفرعي؟ سيصبح الصنف مرئياً للعملاء ومتاحاً للشراء'
          : 'Are you sure you want to activate this sub product? The sub product will become visible to customers and available for purchase.',
        confirmContent: ar
          ? loading
            ? 'جارٍ التفعيل...'
            : 'تأكيد التفعيل'
          : loading
            ? 'Activating...'
            : 'Confirm Activation',
      },
    };

    return content[type!];
  });

  @HostListener('document:click')
  closeDropdown() {
    this.isDropdownOpen.set(false);
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen.update((v) => !v);
  }
}
